// Generated by GitHub Copilot
import { State, useCustomGlobalState, type PropertySelector } from "#core/state/State.ts";

/**
 * Global connectivity state. Tracks whether the browser currently reports a
 * network connection (`navigator.onLine`) and flips reactively on the window
 * `online` / `offline` events, so any component can subscribe with
 * {@link useNetworkState} and the app shell can surface an offline banner and a
 * sync-status chip.
 */
export class NetworkState extends State<NetworkState> {
    static _instance: NetworkState | null = null;

    static {
        // Static initializer runs once (on first import). Reflect connectivity
        // changes onto the singleton via `reactTo` so subscribed components
        // re-render, mirroring how WindowState wires its resize listener.
        if (typeof window !== "undefined") {
            const update = (online: boolean) => {
                const instance = NetworkState._instance?.reactTo;
                if (!instance) return;
                if (online && !instance.online) instance.justReconnected = true;
                instance.online = online;
            };
            window.addEventListener("online", () => update(true));
            window.addEventListener("offline", () => update(false));
        }
    }

    /** True while the browser reports a network connection. */
    online: boolean = typeof navigator !== "undefined" ? navigator.onLine : true;

    /**
     * Set briefly `true` on an offline→online transition so the banner can flash
     * a short "back online" confirmation. Cleared via {@link acknowledgeReconnect}.
     */
    justReconnected: boolean = false;

    constructor() {
        super(undefined, null);
        NetworkState._instance = this;
    }

    /** Clears the transient reconnect flag once the "back online" flash has been shown. */
    public acknowledgeReconnect() {
        if (this.justReconnected) this.reactTo.justReconnected = false;
    }
}

/**
 * Subscribe to connectivity in a component. Pass a filter to react to a single property.
 *
 * Registered under an explicit, stable key ("NetworkState") instead of the default
 * `stateType.name`. Production minification can mangle two different state classes to
 * the SAME class name (here `NetworkState` and `WindowState` both minified to `e`),
 * which made `StateView.getInstance` return whichever singleton was constructed first —
 * `WindowState`, which has no `online` field. `net.online` was then `undefined`, so the
 * offline banner rendered permanently (only in the minified Azure build; local dev keeps
 * real class names so the collision never surfaced). An explicit string key survives
 * minification and cannot collide.
 */
export function useNetworkState(filter: PropertySelector<NetworkState> | undefined = undefined): NetworkState {
    return useCustomGlobalState(() => new NetworkState(), "NetworkState", filter);
}
